// secPose v2 carry-forward seeding.
//
// v2 sessions have no predicted keypoints. This hook lazily seeds each frame's
// starting pose as the annotator navigates:
//   - first navigable frame: default pose (`config/defaultPose`)
//   - later frames: previous navigable frame's current pose
//
// Seeds are written as `seeded:true` edits, which do not count as modifications.

import * as React from 'react';
import { defaultPose } from '../config/defaultPose';
import { KEYPOINTS, visibleKeypoints } from '../config/keypoints';
import { type AppState, useStore } from '../store';
import { frameHasNoEdits } from '../store/edits';
import type { Side } from '../store/predictions';
import type { EditedKeypoint, EditMap, FrameMap, KeypointName } from '../types';

type SideSeed = Partial<Record<KeypointName, EditedKeypoint>>;

function predsFor(s: AppState, side: Side): FrameMap | null {
  if (side === 'front_left') return s.predFrontLeft;
  if (side === 'front_right') return s.predFrontRight;
  if (side === 'left') return s.predLeft;
  return s.predRight;
}

function editsFor(s: AppState, side: Side): EditMap {
  if (side === 'front_left') return s.editsFrontLeft;
  if (side === 'front_right') return s.editsFrontRight;
  if (side === 'left') return s.editsLeft;
  return s.editsRight;
}

function isValidSize(size: [number, number] | undefined): size is [number, number] {
  return !!size && size[0] > 0 && size[1] > 0;
}

function poseSize(
  s: AppState,
  side: Side,
  imageSize: [number, number],
  imageSizes: Partial<Record<Side, [number, number]>>
): [number, number] | null {
  if (isValidSize(imageSizes[side])) return imageSizes[side]!;
  if (isValidSize(imageSize)) return imageSize;
  if (isValidSize(s.folderMeta?.imageSize)) return s.folderMeta!.imageSize;
  return null;
}

function effectiveAt(
  preds: FrameMap | null,
  edits: EditMap,
  frame: number,
  name: KeypointName
): { xy: [number, number]; visibility: EditedKeypoint['visibility'] } | null {
  const edit = edits[frame]?.[name];
  if (edit) return { xy: [edit.xy[0], edit.xy[1]], visibility: edit.visibility };
  const pred = preds?.[frame]?.find((r) => r.name === name);
  if (!pred) return null;
  return { xy: [pred.xy[0], pred.xy[1]], visibility: pred.defaultVisibility };
}

export function useCarryForwardSeed(): void {
  const annotationMode = useStore((s) => s.annotationMode);
  const currentFrame = useStore((s) => s.currentFrame);
  const navigableFrames = useStore((s) => s.navigableFrames);
  const imageSize = useStore((s) => s.imageSize);
  const imageSizes = useStore((s) => s.imageSizes);
  const legacySeedFrames = useStore((s) => s.legacySeedFrames);

  React.useEffect(() => {
    if (annotationMode !== 'v2') return;
    const s = useStore.getState();
    const nav = navigableFrames;
    if (!nav || nav.length === 0) return;
    const frame = currentFrame;
    const idx = nav.indexOf(frame);
    if (idx < 0) return;

    // Committed frames (manual edit exists, or frame was saved) are frozen.
    if (!frameHasNoEdits(s, frame) || s.auditedFrames.includes(frame)) return;

    const seeds: Partial<Record<Side, SideSeed>> = {};

    if (idx === 0) {
      for (const side of s.availableSides) {
        if (legacySeedFrames[side]?.includes(frame)) continue;
        const size = poseSize(s, side, imageSize, imageSizes);
        if (!size) continue;
        const pose = defaultPose(size);
        const visible = visibleKeypoints(side);
        const sideSeed: SideSeed = {};
        for (const { name } of KEYPOINTS) {
          if (!visible.has(name)) continue;
          sideSeed[name] = { xy: pose[name], visibility: 'visible', locked: false };
        }
        seeds[side] = sideSeed;
      }
    } else {
      const prev = nav[idx - 1]!;
      for (const side of s.availableSides) {
        if (legacySeedFrames[side]?.includes(frame)) continue;
        const preds = predsFor(s, side);
        const edits = editsFor(s, side);
        const visible = visibleKeypoints(side);
        const sideSeed: SideSeed = {};
        for (const { name } of KEYPOINTS) {
          if (!visible.has(name)) continue;
          const eff = effectiveAt(preds, edits, prev, name);
          if (!eff) continue;
          sideSeed[name] = { xy: eff.xy, visibility: eff.visibility, locked: false };
        }
        seeds[side] = sideSeed;
      }
    }

    s.seedFrameEdits(frame, seeds);
  }, [annotationMode, currentFrame, navigableFrames, imageSize, imageSizes, legacySeedFrames]);
}
